import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import Topbar from '../components/Topbar'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts'

const BLUE   = '#1877F2'
const TEXT   = '#050505'
const MUTED  = '#65676B'
const BORDER = '#E4E6EB'
const BG     = '#F0F2F5'
const WHITE  = '#FFFFFF'
const GREEN  = '#16A34A'
const AMBER  = '#D97706'
const RED    = '#DC2626'
const PURPLE = '#7C3AED'

const BOOSTS_PER_PAGE = 10

const inp = {
  width: '100%', border: '1px solid ' + BORDER, borderRadius: '9px',
  padding: '9px 12px', fontSize: '13px', outline: 'none',
  background: WHITE, color: TEXT, fontFamily: 'Space Grotesk, system-ui, sans-serif',
  boxSizing: 'border-box',
}

const BOOST_TYPES = [
  { key: 'post',         label: 'Existing Post',      desc: 'Boost a post already on your Facebook page' },
  { key: 'video_reel',   label: 'Video / Reel',       desc: 'Boost an existing video or reel' },
  { key: 'live_session', label: 'Live Replay',        desc: 'Boost a recorded live session' },
  { key: 'page',         label: 'Page Likes',         desc: 'Get more followers for your Facebook page' },
  { key: 'product',      label: 'Product Campaign',   desc: 'Promote a specific product with audience targeting' },
  { key: 'website',      label: 'Link / Website',     desc: 'Drive traffic to any URL' },
]

const GOALS_BY_TYPE = {
  post:         [{ key: 'reach', label: 'Post Reach' }, { key: 'engagement', label: 'Engagement' }, { key: 'messages', label: 'Messages' }],
  video_reel:   [{ key: 'video_views', label: 'Video Views' }, { key: 'reach', label: 'Reach' }, { key: 'engagement', label: 'Engagement' }],
  live_session: [{ key: 'video_views', label: 'Views' }, { key: 'reach', label: 'Reach' }],
  page:         [{ key: 'page_likes', label: 'Page Likes' }, { key: 'followers', label: 'Followers' }],
  product:      [{ key: 'messages', label: 'Messages' }, { key: 'traffic', label: 'Traffic' }, { key: 'reach', label: 'Reach' }],
  website:      [{ key: 'traffic', label: 'Traffic' }, { key: 'conversions', label: 'Conversions' }],
}

const BUDGET_PRESETS   = [500, 1000, 2000, 5000]
const DURATION_PRESETS = [3, 5, 7, 14]

const STATUS_CONFIG = {
  pending:   { label: 'Payment Pending', color: AMBER, bg: '#FFFBEB' },
  paid:      { label: 'Being Prepared',  color: BLUE,  bg: '#EFF6FF' },
  preparing: { label: 'Being Prepared',  color: BLUE,  bg: '#EFF6FF' },
  running:   { label: 'Live',            color: GREEN, bg: '#F0FDF4' },
  completed: { label: 'Completed',       color: MUTED, bg: BG        },
  cancelled: { label: 'Cancelled',       color: RED,   bg: '#FEF2F2' },
}

const PIE_COLORS = [BLUE, GREEN, AMBER, PURPLE, RED, '#F97316']

const STEP_LABELS = ['What to Boost', 'Details', 'Budget & Duration', 'Creative', 'Review & Pay']

// ── Icons ──
function Icon({ path, size = 15, color = MUTED, strokeWidth = 1.8 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  )
}

const IC = {
  search:   'M21 21l-4.35-4.35 M11 19a8 8 0 100-16 8 8 0 000 16z',
  x:        'M6 18L18 6M6 6l12 12',
  chevL:    'M15 18l-6-6 6-6',
  chevR:    'M9 18l6-6-6-6',
  eye:      'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 12m-3 0a3 3 0 106 0 3 3 0 00-6 0',
  download: 'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4 M7 10l5 5 5-5 M12 15V3',
  plus:     'M12 4v16m8-8H4',
  boost:    'M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z',
  chart:    'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  calendar: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  info:     'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  check:    'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  clock:    'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  money:    'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  trending: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
  users:    'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2 M23 21v-2a4 4 0 00-3-3.87 M16 3.13a4 4 0 010 7.75',
  arrow:    'M9 18l6-6-6-6',
}

function ActionBtn({ onClick, title, iconPath, hoverBg, hoverColor }) {
  const [hovered, setHovered] = useState(false)
  return (
    <button onClick={onClick} title={title}
      style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '7px', border: 'none', cursor: 'pointer', background: hovered ? hoverBg : 'transparent', transition: 'background 0.12s' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      <Icon path={iconPath} size={13} color={hovered ? hoverColor : MUTED} strokeWidth={2} />
    </button>
  )
}

function Label({ children, optional }) {
  return (
    <div style={{ fontSize: '12px', fontWeight: 600, color: MUTED, marginBottom: '6px' }}>
      {children}{optional && <span style={{ fontWeight: 400, color: '#B0B3B8', marginLeft: '4px' }}>(optional)</span>}
    </div>
  )
}

function InfoBox({ title, children, color = '#EFF6FF', border = '#BFDBFE', text = '#1E40AF' }) {
  return (
    <div style={{ background: color, border: `1px solid ${border}`, borderRadius: '10px', padding: '12px 16px', marginBottom: '14px' }}>
      {title && <div style={{ fontSize: '12px', fontWeight: 700, color: text, marginBottom: '5px' }}>{title}</div>}
      <div style={{ fontSize: '12px', color: text, lineHeight: 1.65 }}>{children}</div>
    </div>
  )
}

function ProgressBar({ launched_at, ends_at }) {
  if (!launched_at || !ends_at) return null
  const start    = new Date(launched_at).getTime()
  const end      = new Date(ends_at).getTime()
  const now      = Date.now()
  const pct      = Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100))
  const daysLeft = Math.ceil((end - now) / 86400000)
  const total    = Math.ceil((end - start) / 86400000)
  const done     = total - Math.max(0, daysLeft)
  const ending   = daysLeft <= 1
  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span style={{ fontSize: '10.5px', color: MUTED }}>Day {done} of {total}</span>
        <span style={{ fontSize: '10.5px', color: ending ? RED : MUTED, fontWeight: ending ? 700 : 400 }}>
          {daysLeft <= 0 ? 'Ending today' : daysLeft === 1 ? 'Ends tomorrow' : `${daysLeft} days left`}
        </span>
      </div>
      <div style={{ height: '4px', background: BORDER, borderRadius: '99px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: pct + '%', background: ending ? RED : BLUE, borderRadius: '99px', transition: 'width 0.4s ease' }} />
      </div>
    </div>
  )
}

// ── Detail Modal ──
function DetailModal({ boost, onClose }) {
  const cfg         = STATUS_CONFIG[boost.status] || STATUS_CONFIG.pending
  const typeLabel   = BOOST_TYPES.find(t => t.key === boost.boost_type)?.label || boost.boost_type
  const submitted   = new Date(boost.created_at)
  const launched    = boost.launched_at ? new Date(boost.launched_at) : null
  const hoursToLive = launched ? Math.round((launched - submitted) / 3600000) : null

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: WHITE, borderRadius: '16px', width: '100%', maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid ' + BORDER, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'sticky', top: 0, background: WHITE, zIndex: 1 }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 800, color: TEXT }}>{boost.fb_campaign_name}</div>
            <div style={{ fontSize: '12px', color: MUTED, marginTop: '2px' }}>{typeLabel} · {boost.goal?.replace(/_/g, ' ')}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, color: cfg.color, background: cfg.bg, padding: '3px 10px', borderRadius: '20px' }}>{cfg.label}</span>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
              <Icon path={IC.x} color={MUTED} size={16} />
            </button>
          </div>
        </div>

        <div style={{ padding: '20px' }}>

          {/* Submission timing notice */}
          {hoursToLive !== null && (
            <div style={{ background: hoursToLive <= 24 ? '#F0FDF4' : '#EFF6FF', border: `1px solid ${hoursToLive <= 24 ? '#BBF7D0' : '#BFDBFE'}`, borderRadius: '10px', padding: '10px 14px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Icon path={IC.clock} color={hoursToLive <= 24 ? GREEN : BLUE} size={16} strokeWidth={2} />
              <div style={{ fontSize: '12px', color: hoursToLive <= 24 ? '#065F46' : '#1E40AF' }}>
                <strong>Time to launch: {hoursToLive}h</strong> — from submission to campaign going live.
              </div>
            </div>
          )}

          {boost.status === 'running' && <ProgressBar launched_at={boost.launched_at} ends_at={boost.ends_at} />}

          {/* Timeline */}
          <div style={{ marginTop: '20px', marginBottom: '20px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '14px' }}>Campaign Timeline</div>
            {[
              { done: true,                        label: 'Request submitted',   date: boost.created_at },
              { done: !!boost.payment_time,        label: 'Payment confirmed',   date: boost.payment_time },
              { done: !!boost.launched_at,         label: 'Campaign launched',   date: boost.launched_at },
              { done: !!boost.completed_at,        label: 'Campaign completed',  date: boost.ends_at },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: item.done ? BLUE : BORDER, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                  {item.done && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                </div>
                <div>
                  <div style={{ fontSize: '12.5px', fontWeight: 600, color: item.done ? TEXT : MUTED }}>{item.label}</div>
                  {item.date && <div style={{ fontSize: '11px', color: MUTED, marginTop: '1px' }}>{new Date(item.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>}
                </div>
              </div>
            ))}
          </div>

          {/* FB URL */}
          {boost.fb_url && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '6px' }}>Boosted Content</div>
              <a href={boost.fb_url} target="_blank" rel="noreferrer" style={{ fontSize: '12px', color: BLUE, wordBreak: 'break-all' }}>{boost.fb_url}</a>
            </div>
          )}

          {/* Payment summary */}
          <div style={{ background: BG, borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px' }}>Payment Receipt</div>
            {[
              { label: 'Ad Budget (to Facebook)',              val: '৳' + Number(boost.budget_bdt || 0).toLocaleString() },
              { label: `Service Fee (${boost.service_fee_pct}%)`, val: '৳' + Number(boost.service_fee_amount || 0).toLocaleString() },
              { label: 'VAT on service fee (15%)',             val: '৳' + Number(boost.vat_amount || 0).toLocaleString() },
              { label: 'Total Paid',                           val: '৳' + Number(boost.total_payable || 0).toLocaleString(), bold: true },
              { label: 'Payment Method',                       val: boost.payment_method || '—' },
              { label: 'USD Rate at Submission',               val: `৳${boost.usd_rate_used}/USD (PageConnect rate)` },
              { label: 'Estimated Ad Spend',                   val: '$' + Number(boost.budget_usd || 0).toFixed(2) + ' USD' },
            ].map(r => (
              <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid ' + BORDER, gap: '12px' }}>
                <span style={{ fontSize: '12px', color: MUTED }}>{r.label}</span>
                <span style={{ fontSize: '12px', fontWeight: r.bold ? 800 : 600, color: r.bold ? BLUE : TEXT, textAlign: 'right' }}>{r.val}</span>
              </div>
            ))}
          </div>

          {/* Audience */}
          {boost.audience_notes && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '6px' }}>Audience Notes</div>
              <div style={{ fontSize: '12.5px', color: TEXT, lineHeight: 1.5 }}>{boost.audience_notes}</div>
            </div>
          )}

          {/* Results */}
          {boost.result_reach && (
            <div style={{ background: '#EFF6FF', borderRadius: '10px', padding: '14px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: BLUE, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px' }}>Campaign Results</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '10px', marginBottom: '10px' }}>
                {[
                  { label: 'Reach',        val: boost.result_reach?.toLocaleString() },
                  { label: 'Impressions',  val: boost.result_impressions?.toLocaleString() },
                  { label: 'Clicks',       val: boost.result_clicks?.toLocaleString() },
                  { label: 'Engagements',  val: boost.result_engagements?.toLocaleString() },
                  boost.fb_page_followers_start && boost.fb_page_followers_end && {
                    label: 'New Followers',
                    val: '+' + (boost.fb_page_followers_end - boost.fb_page_followers_start).toLocaleString()
                  },
                ].filter(Boolean).filter(r => r.val && r.val !== 'undefined').map(r => (
                  <div key={r.label} style={{ background: WHITE, borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                    <div style={{ fontSize: '16px', fontWeight: 800, color: BLUE }}>{r.val}</div>
                    <div style={{ fontSize: '10.5px', color: MUTED, marginTop: '2px' }}>{r.label}</div>
                  </div>
                ))}
              </div>
              {boost.result_notes && <div style={{ fontSize: '12px', color: MUTED, lineHeight: 1.5 }}>{boost.result_notes}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default function AdBoost() {
  const { seller } = useAuth()
  const { showToast } = useToast()

  const [mainTab, setMainTab]       = useState('list') // list | new | insights | howto
  const [step, setStep]             = useState(1)
  const [boosts, setBoosts]         = useState([])
  const [products, setProducts]     = useState([])
  const [loading, setLoading]       = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [feeSettings, setFeeSettings] = useState(null)
  const [activeRate, setActiveRate]   = useState(null)
  const [insights, setInsights]       = useState([])
  const [selectedBoost, setSelectedBoost] = useState(null)

  // List filters
  const [search, setSearch]           = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [dateFrom, setDateFrom]       = useState('')
  const [dateTo, setDateTo]           = useState('')
  const [currentPage, setCurrentPage] = useState(1)

  // Charts data
  const [spendData, setSpendData]   = useState([])
  const [statusPie, setStatusPie]   = useState([])

  const [form, setForm] = useState({
    boost_type: '', fb_url: '', product_id: '', product_name: '',
    goal: '', budget_bdt: '', duration_days: '', fb_page_followers_start: '',
    audience_notes: '', creative_url: null, creative_request: '',
    creative_option: '', payment_method: '', scheduled_start_date: '',
  })
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  useEffect(() => {
    if (seller?.id) {
      setLoading(true)
      Promise.all([fetchBoosts(), fetchFeeSettings(), fetchProducts(), fetchInsights()])
        .then(() => setLoading(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seller?.id])

  useEffect(() => { setCurrentPage(1) }, [search, filterStatus, dateFrom, dateTo])

  const fetchBoosts = async () => {
    const { data } = await supabase.from('boosts').select('*').eq('seller_id', seller.id).order('created_at', { ascending: false })
    const arr = data || []
    setBoosts(arr)
    buildCharts(arr)
  }

  const buildCharts = (arr) => {
    // Monthly spend over last 6 months
    const monthMap = {}
    for (let i = 5; i >= 0; i--) {
      const d = new Date(); d.setMonth(d.getMonth() - i)
      const key = d.toLocaleDateString('en-BD', { month: 'short', year: '2-digit' })
      monthMap[key] = { month: key, spend: 0, reach: 0 }
    }
    arr.forEach(b => {
      const key = new Date(b.created_at).toLocaleDateString('en-BD', { month: 'short', year: '2-digit' })
      if (monthMap[key]) {
        monthMap[key].spend += Number(b.total_payable || 0)
        monthMap[key].reach += Number(b.result_reach || 0)
      }
    })
    setSpendData(Object.values(monthMap))

    // Status pie
    const statusCount = {}
    arr.forEach(b => { statusCount[b.status] = (statusCount[b.status] || 0) + 1 })
    setStatusPie(Object.entries(statusCount).map(([status, value]) => ({
      status, value, label: STATUS_CONFIG[status]?.label || status,
      color: STATUS_CONFIG[status]?.color || MUTED,
    })))
  }

  const fetchFeeSettings = async () => {
    const market = seller?.country === 'BD' || !seller?.country ? 'BD' : 'INT'
    const { data: fee } = await supabase.from('boost_fee_settings').select('*').eq('market', market).eq('is_active', true).single()
    setFeeSettings(fee)
    const { data: rate } = await supabase.from('boost_usd_rates').select('*').eq('is_active', true).single()
    setActiveRate(rate)
  }

  const fetchProducts = async () => {
    const { data } = await supabase.from('products').select('id, name, sell_price').eq('seller_id', seller.id).order('name')
    setProducts(data || [])
  }

  const fetchInsights = async () => {
    const { data: orders } = await supabase.from('orders').select('product_name').eq('seller_id', seller.id).gte('created_at', new Date(Date.now() - 30 * 86400000).toISOString())
    const counts = {}
    orders?.forEach(o => { if (o.product_name) counts[o.product_name] = (counts[o.product_name] || 0) + 1 })
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 3)
    const list = sorted.map(([name, count]) => ({
      title: `"${name}" sold ${count} times this month`,
      desc: 'Strong demand. Boosting this product can reach more potential buyers and grow sales further.',
    }))
    list.push({ title: 'Best time to boost in Bangladesh', desc: 'Friday evenings 8–10 PM and Saturday mornings show the highest engagement for F-commerce sellers. Schedule your boost to go live at these times.' })
    list.push({ title: 'Page likes drive long-term growth', desc: 'Every new follower is a potential future customer. A page likes campaign is a long-term investment even if it does not generate immediate orders.' })
    setInsights(list)
  }

  const calcPricing = () => {
    const budget = Number(form.budget_bdt) || 0
    const svcPct = feeSettings?.service_fee_pct || 15
    const vatPct = feeSettings?.vat_pct || 15
    const rate   = activeRate?.pageconnect_rate || 120
    const svcFee = budget * svcPct / 100
    const vat    = svcFee * vatPct / 100
    const total  = budget + svcFee + vat
    const usd    = budget / rate
    return { budget, svcFee, vat, total, usd, svcPct, vatPct, rate }
  }

  const genCampaignName = () => {
    const slug    = (seller?.shop_name || 'Seller').replace(/\s+/g, '').slice(0, 12)
    const typeMap = { post: 'Post', video_reel: 'Video', live_session: 'Live', page: 'Page', product: 'Product', website: 'Link' }
    const goalMap = { reach: 'Reach', engagement: 'Engage', messages: 'Msgs', page_likes: 'Likes', followers: 'Follow', video_views: 'Views', traffic: 'Traffic', conversions: 'Convert' }
    const num     = String(boosts.length + 1).padStart(4, '0')
    return `PC${num}-${slug}-${typeMap[form.boost_type] || 'Boost'}-${goalMap[form.goal] || 'BD'}`
  }

  const handleSubmit = async () => {
    if (!form.payment_method) { showToast('Select a payment method', 'error'); return }
    setSubmitting(true)
    const pricing = calcPricing()
    const { error } = await supabase.from('boosts').insert({
      seller_id: seller.id,
      market: seller?.country === 'BD' || !seller?.country ? 'BD' : 'INT',
      boost_type: form.boost_type, fb_url: form.fb_url || null,
      product_id: form.product_id || null, product_name: form.product_name || null,
      goal: form.goal, budget_bdt: pricing.budget, budget_usd: pricing.usd,
      usd_rate_used: pricing.rate, service_fee_pct: pricing.svcPct,
      service_fee_amount: pricing.svcFee, vat_amount: pricing.vat,
      total_payable: pricing.total, duration_days: Number(form.duration_days),
      scheduled_start_date: form.scheduled_start_date || null,
      fb_page_followers_start: form.fb_page_followers_start ? Number(form.fb_page_followers_start) : null,
      audience_notes: form.audience_notes || null,
      creative_request: form.creative_request || null,
      payment_method: form.payment_method, payment_status: 'pending', status: 'pending',
      fb_campaign_name: genCampaignName(), terms_accepted: true, terms_accepted_at: new Date().toISOString(),
    })
    if (error) { showToast('Failed to submit: ' + error.message, 'error'); setSubmitting(false); return }
    showToast('Campaign request submitted!')
    setForm({ boost_type: '', fb_url: '', product_id: '', product_name: '', goal: '', budget_bdt: '', duration_days: '', fb_page_followers_start: '', audience_notes: '', creative_url: null, creative_request: '', creative_option: '', payment_method: '', scheduled_start_date: '' })
    setStep(1)
    setMainTab('list')
    fetchBoosts()
    setSubmitting(false)
  }

  const pricing = calcPricing()

  // ── Filtering ──
  const filtered = boosts.filter(b => {
    const q = search.toLowerCase()
    const matchSearch = !search ||
      b.fb_campaign_name?.toLowerCase().includes(q) ||
      b.goal?.toLowerCase().includes(q) ||
      b.boost_type?.toLowerCase().includes(q)
    const matchStatus = filterStatus === 'all' || b.status === filterStatus
    const d = new Date(b.created_at)
    const matchFrom = !dateFrom || d >= new Date(dateFrom)
    const matchTo   = !dateTo   || d <= new Date(dateTo + 'T23:59:59')
    return matchSearch && matchStatus && matchFrom && matchTo
  })

  const totalPages = Math.ceil(filtered.length / BOOSTS_PER_PAGE)
  const pageStart  = (currentPage - 1) * BOOSTS_PER_PAGE
  const pageEnd    = Math.min(pageStart + BOOSTS_PER_PAGE, filtered.length)
  const pageBoosts = filtered.slice(pageStart, pageEnd)

  // ── Summary stats ──
  const totalSpent   = boosts.reduce((s, b) => s + (Number(b.total_payable) || 0), 0)
  const totalAdSpend = boosts.reduce((s, b) => s + (Number(b.budget_bdt) || 0), 0)
  const totalReach   = boosts.reduce((s, b) => s + (Number(b.result_reach) || 0), 0)
  const totalClicks  = boosts.reduce((s, b) => s + (Number(b.result_clicks) || 0), 0)
  const activeCount  = boosts.filter(b => b.status === 'running').length

  const MAIN_TABS = [
    { key: 'list',     label: 'All Campaigns' },
    { key: 'new',      label: '+ New Boost' },
    { key: 'insights', label: 'Insights' },
    { key: 'howto',    label: 'How It Works' },
  ]

  const COLS = '36px 160px 120px 110px 90px 100px 110px 90px 100px'

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: BG, fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
      <Topbar title="AdBoost" />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED }}>Loading...</div>
    </div>
  )

  return (
    <div style={{ flex: 1, minHeight: '100vh', overflowX: 'hidden', background: BG, fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
      <Topbar title="AdBoost" />
      <div style={{ padding: '18px 24px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: TEXT }}>AdBoost</div>
            <div style={{ fontSize: '12px', color: MUTED, marginTop: '2px' }}>Facebook advertising managed by PageConnect — pay in bKash or Nagad</div>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            {activeRate && (
              <div style={{ background: WHITE, border: '1px solid ' + BORDER, borderRadius: '10px', padding: '7px 14px', fontSize: '12px' }}>
                <span style={{ color: MUTED }}>PageConnect USD rate: </span>
                <strong style={{ color: TEXT }}>৳{activeRate.pageconnect_rate}/USD</strong>
                <span style={{ fontSize: '10.5px', color: MUTED, marginLeft: '4px' }}>incl. conversion fees · {activeRate.effective_from}</span>
              </div>
            )}
            <button onClick={() => { setMainTab('new'); setStep(1) }}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '10px', background: BLUE, border: 'none', fontSize: '13px', fontWeight: 600, color: WHITE, cursor: 'pointer', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
              <Icon path={IC.plus} color={WHITE} size={14} strokeWidth={2.5} />
              New Boost
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', padding: '4px', borderRadius: '12px', border: '1px solid ' + BORDER, background: WHITE, width: 'fit-content', flexWrap: 'wrap' }}>
          {MAIN_TABS.map(tab => (
            <button key={tab.key} onClick={() => { setMainTab(tab.key); setStep(1) }}
              style={{ padding: '7px 16px', borderRadius: '9px', background: mainTab === tab.key ? BLUE : 'transparent', color: mainTab === tab.key ? WHITE : MUTED, border: 'none', cursor: 'pointer', fontSize: '12.5px', fontWeight: 600, transition: 'all 0.12s', fontFamily: 'Space Grotesk, system-ui, sans-serif', whiteSpace: 'nowrap' }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── ALL CAMPAIGNS ── */}
        {mainTab === 'list' && (
          <>
            {/* Summary KPI cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', marginBottom: '16px' }} className="ab-stat-grid">
              {[
                { label: 'Total Campaigns', val: boosts.length,                     icon: IC.boost,   accent: BLUE   },
                { label: 'Active Now',       val: activeCount,                        icon: IC.check,   accent: GREEN  },
                { label: 'Total Paid',       val: '৳' + Math.round(totalSpent).toLocaleString(), icon: IC.money, accent: BLUE },
                { label: 'Total Ad Spend',   val: '৳' + Math.round(totalAdSpend).toLocaleString(), icon: IC.chart, accent: PURPLE },
                { label: 'Total Reach',      val: totalReach > 0 ? totalReach.toLocaleString() : '—', icon: IC.users, accent: AMBER },
              ].map(c => (
                <div key={c.label} style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '14px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <div style={{ fontSize: '11.5px', fontWeight: 500, color: MUTED }}>{c.label}</div>
                    <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: c.accent + '15', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon path={c.icon} color={c.accent} size={14} strokeWidth={2} />
                    </div>
                  </div>
                  <div style={{ fontSize: '22px', fontWeight: 700, color: TEXT, letterSpacing: '-0.3px' }}>{c.val}</div>
                </div>
              ))}
            </div>

            {/* Charts */}
            {boosts.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px', marginBottom: '16px' }} className="ab-chart-grid">
                {/* Spend + Reach area chart */}
                <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px 18px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT, marginBottom: '2px' }}>Monthly Spend & Reach</div>
                  <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '12px' }}>Last 6 months</div>
                  <ResponsiveContainer width="100%" height={130}>
                    <AreaChart data={spendData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="gradSpend" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor={BLUE}  stopOpacity={0.2} />
                          <stop offset="95%" stopColor={BLUE}  stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="gradReach" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor={GREEN} stopOpacity={0.2} />
                          <stop offset="95%" stopColor={GREEN} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 10, fill: MUTED }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: MUTED }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ border: '1px solid ' + BORDER, borderRadius: '8px', fontFamily: 'Space Grotesk', fontSize: '11px' }}
                        formatter={(v, n) => [n === 'spend' ? '৳' + Math.round(v).toLocaleString() : v.toLocaleString(), n === 'spend' ? 'Ad Spend' : 'Reach']} />
                      <Area type="monotone" dataKey="spend" stroke={BLUE} strokeWidth={2} fill="url(#gradSpend)" dot={{ fill: BLUE, r: 3, strokeWidth: 0 }} />
                      <Area type="monotone" dataKey="reach" stroke={GREEN} strokeWidth={2} fill="url(#gradReach)" dot={{ fill: GREEN, r: 3, strokeWidth: 0 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                  <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
                    {[{ color: BLUE, label: 'Ad Spend (৳)' }, { color: GREEN, label: 'Reach' }].map(l => (
                      <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{ width: '16px', height: '2.5px', background: l.color, borderRadius: '2px' }} />
                        <span style={{ fontSize: '11px', color: MUTED }}>{l.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Status pie */}
                <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px 18px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT, marginBottom: '2px' }}>Campaign Status</div>
                  <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '8px' }}>Breakdown by status</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <ResponsiveContainer width={90} height={90}>
                      <PieChart>
                        <Pie data={statusPie} cx="50%" cy="50%" innerRadius={26} outerRadius={42} paddingAngle={2} dataKey="value" strokeWidth={0} isAnimationActive animationBegin={200} animationDuration={600}>
                          {statusPie.map((entry, i) => <Cell key={entry.status} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ border: '1px solid ' + BORDER, borderRadius: '8px', fontFamily: 'Space Grotesk', fontSize: '11px' }} formatter={(v, n, p) => [v, p.payload.label]} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      {statusPie.map((s, i) => (
                        <div key={s.status} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: PIE_COLORS[i % PIE_COLORS.length] }} />
                            <span style={{ fontSize: '10.5px', color: MUTED }}>{s.label}</span>
                          </div>
                          <span style={{ fontSize: '10.5px', fontWeight: 600, color: TEXT }}>{s.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Performance summary */}
                  {totalClicks > 0 && (
                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid ' + BORDER }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ fontSize: '11px', color: MUTED }}>Total Clicks</span>
                        <span style={{ fontSize: '11px', fontWeight: 700, color: TEXT }}>{totalClicks.toLocaleString()}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '11px', color: MUTED }}>Cost per Click</span>
                        <span style={{ fontSize: '11px', fontWeight: 700, color: TEXT }}>৳{totalClicks > 0 ? Math.round(totalAdSpend / totalClicks) : '—'}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Search + filters */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '220px', position: 'relative' }}>
                <input type="text" placeholder="Search campaigns..." value={search} onChange={e => setSearch(e.target.value)}
                  style={{ width: '100%', border: '1px solid ' + BORDER, borderRadius: '10px', padding: '9px 12px 9px 36px', fontSize: '12.5px', outline: 'none', background: WHITE, color: TEXT, fontFamily: 'Space Grotesk', boxSizing: 'border-box' }}
                  onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
                <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }}>
                  <Icon path={IC.search} color={MUTED} size={14} />
                </div>
                {search && <button onClick={() => setSearch('')} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer' }}><Icon path={IC.x} color={MUTED} size={12} /></button>}
              </div>

              {/* Date range */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Icon path={IC.calendar} color={MUTED} size={14} />
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                  style={{ border: '1px solid ' + BORDER, borderRadius: '9px', padding: '8px 10px', fontSize: '12px', outline: 'none', background: WHITE, color: TEXT, fontFamily: 'Space Grotesk', cursor: 'pointer' }}
                  onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
                <span style={{ fontSize: '12px', color: MUTED }}>to</span>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                  style={{ border: '1px solid ' + BORDER, borderRadius: '9px', padding: '8px 10px', fontSize: '12px', outline: 'none', background: WHITE, color: TEXT, fontFamily: 'Space Grotesk', cursor: 'pointer' }}
                  onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
                {(dateFrom || dateTo) && (
                  <button onClick={() => { setDateFrom(''); setDateTo('') }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: MUTED }}>
                    <Icon path={IC.x} color={MUTED} size={12} />
                  </button>
                )}
              </div>
            </div>

            {/* Status filter pills */}
            <div style={{ display: 'flex', gap: '6px', marginBottom: '14px', overflowX: 'auto', paddingBottom: '4px' }}>
              {['all', 'pending', 'paid', 'preparing', 'running', 'completed', 'cancelled'].map(s => (
                <button key={s} onClick={() => setFilterStatus(s)}
                  style={{ padding: '5px 13px', borderRadius: '20px', border: '1px solid', fontSize: '12px', fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0, cursor: 'pointer', transition: 'all 0.12s', background: filterStatus === s ? BLUE : WHITE, color: filterStatus === s ? WHITE : MUTED, borderColor: filterStatus === s ? BLUE : BORDER }}>
                  {s === 'all' ? 'All' : STATUS_CONFIG[s]?.label || s}
                  <span style={{ marginLeft: '5px', opacity: 0.75, fontSize: '11px' }}>
                    {s === 'all' ? boosts.length : boosts.filter(b => b.status === s).length}
                  </span>
                </button>
              ))}
            </div>

            {/* 24hr notice */}
            <InfoBox>
              <strong>Tip:</strong> Submit your boost request at least 24 hours before your desired start date. Our team sets up campaigns within 24 hours of payment confirmation.
            </InfoBox>

            {/* Table */}
            {filtered.length === 0 ? (
              <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '48px', textAlign: 'center' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
                  <Icon path={IC.boost} color={BORDER} size={40} strokeWidth={1.2} />
                </div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: TEXT, marginBottom: '4px' }}>
                  {boosts.length === 0 ? 'No campaigns yet' : 'No campaigns match your filters'}
                </div>
                {boosts.length === 0 && (
                  <button onClick={() => { setMainTab('new'); setStep(1) }}
                    style={{ marginTop: '12px', fontSize: '12.5px', fontWeight: 600, padding: '8px 20px', borderRadius: '9px', background: BLUE, color: WHITE, border: 'none', cursor: 'pointer', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                    Create First Campaign
                  </button>
                )}
              </div>
            ) : (
              <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, overflow: 'hidden' }}>
                {/* Table header — desktop */}
                <div className="hidden md:grid"
                  style={{ gridTemplateColumns: COLS, gap: '8px', padding: '9px 16px', background: BG, borderBottom: '1px solid ' + BORDER }}>
                  {['#', 'Campaign ID', 'Type · Goal', 'Budget', 'Duration', 'Submitted', 'Live Date', 'Status', 'Actions'].map(h => (
                    <div key={h} style={{ fontSize: '10.5px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</div>
                  ))}
                </div>

                {pageBoosts.map((boost, idx) => {
                  const cfg      = STATUS_CONFIG[boost.status] || STATUS_CONFIG.pending
                  const isEnding = boost.status === 'running' && boost.ends_at && new Date(boost.ends_at).getTime() - Date.now() < 86400000
                  const typeLabel = BOOST_TYPES.find(t => t.key === boost.boost_type)?.label || boost.boost_type
                  const slNum    = pageStart + idx + 1
                  const submitted = new Date(boost.created_at)
                  const launched  = boost.launched_at ? new Date(boost.launched_at) : null
                  const hoursToLive = launched ? Math.round((launched - submitted) / 3600000) : null

                  return (
                    <div key={boost.id} style={{ borderBottom: '1px solid #F5F5F5' }}>
                      {/* Desktop row */}
                      <div className="hidden md:grid"
                        onClick={() => setSelectedBoost(boost)}
                        style={{ gridTemplateColumns: COLS, gap: '8px', padding: '11px 16px', alignItems: 'center', cursor: 'pointer', transition: 'background 0.1s' }}
                        onMouseEnter={e => e.currentTarget.style.background = BG}
                        onMouseLeave={e => e.currentTarget.style.background = WHITE}>
                        <div style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED }}>{slNum}</div>
                        <div>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: BLUE, fontFamily: 'JetBrains Mono, monospace', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{boost.fb_campaign_name}</div>
                          {boost.status === 'running' && <div style={{ marginTop: '4px' }}><ProgressBar launched_at={boost.launched_at} ends_at={boost.ends_at} /></div>}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{typeLabel}</div>
                          <div style={{ fontSize: '11px', color: MUTED, textTransform: 'capitalize' }}>{boost.goal?.replace(/_/g, ' ')}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '12.5px', fontWeight: 700, color: TEXT }}>৳{Number(boost.budget_bdt || 0).toLocaleString()}</div>
                          <div style={{ fontSize: '10.5px', color: MUTED }}>${Number(boost.budget_usd || 0).toFixed(2)}</div>
                        </div>
                        <div style={{ fontSize: '12px', color: TEXT }}>{boost.duration_days}d</div>
                        <div>
                          <div style={{ fontSize: '11.5px', color: TEXT }}>{submitted.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</div>
                          <div style={{ fontSize: '10.5px', color: MUTED }}>{submitted.toLocaleTimeString('en-BD', { hour: '2-digit', minute: '2-digit' })}</div>
                        </div>
                        <div>
                          {launched
                            ? <>
                                <div style={{ fontSize: '11.5px', color: TEXT }}>{launched.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</div>
                                {hoursToLive !== null && <div style={{ fontSize: '10px', color: hoursToLive <= 24 ? GREEN : MUTED }}>{hoursToLive}h to live</div>}
                              </>
                            : <div style={{ fontSize: '11.5px', color: MUTED }}>—</div>}
                        </div>
                        <div>
                          <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 9px', borderRadius: '20px', background: isEnding ? '#FEF2F2' : cfg.bg, color: isEnding ? RED : cfg.color, whiteSpace: 'nowrap' }}>
                            {isEnding ? 'Ending Today' : cfg.label}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }} onClick={e => e.stopPropagation()}>
                          <ActionBtn onClick={() => setSelectedBoost(boost)} title="View Details" iconPath={IC.eye} hoverBg={BLUE + '15'} hoverColor={BLUE} />
                          {boost.status === 'completed' && boost.result_reach && (
                            <ActionBtn onClick={() => setSelectedBoost(boost)} title="View Report" iconPath={IC.chart} hoverBg="#F0FDF4" hoverColor={GREEN} />
                          )}
                        </div>
                      </div>

                      {/* Mobile card */}
                      <div className="md:hidden" style={{ padding: '14px 16px' }} onClick={() => setSelectedBoost(boost)}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: BLUE, fontFamily: 'JetBrains Mono, monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: '8px' }}>{boost.fb_campaign_name}</div>
                          <span style={{ fontSize: '10.5px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px', background: cfg.bg, color: cfg.color, flexShrink: 0 }}>{cfg.label}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div style={{ fontSize: '12px', color: MUTED }}>{typeLabel} · {boost.goal?.replace(/_/g, ' ')}</div>
                          <div style={{ fontSize: '12.5px', fontWeight: 700, color: TEXT }}>৳{Number(boost.budget_bdt || 0).toLocaleString()}</div>
                        </div>
                        {boost.status === 'running' && <ProgressBar launched_at={boost.launched_at} ends_at={boost.ends_at} />}
                      </div>
                    </div>
                  )
                })}

                {/* Pagination */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderTop: '1px solid ' + BORDER, background: BG, flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ fontSize: '12px', color: MUTED }}>
                    Showing <span style={{ fontWeight: 600, color: TEXT }}>{pageStart + 1}–{pageEnd}</span> of <span style={{ fontWeight: 600, color: TEXT }}>{filtered.length}</span> campaigns
                  </div>
                  {totalPages > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                        style={{ width: '30px', height: '30px', borderRadius: '8px', border: '1px solid ' + BORDER, background: WHITE, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.4 : 1 }}>
                        <Icon path={IC.chevL} color={TEXT} size={13} strokeWidth={2} />
                      </button>
                      {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                        let page
                        if (totalPages <= 7) page = i + 1
                        else if (currentPage <= 4) page = i + 1
                        else if (currentPage >= totalPages - 3) page = totalPages - 6 + i
                        else page = currentPage - 3 + i
                        if (page < 1 || page > totalPages) return null
                        return (
                          <button key={page} onClick={() => setCurrentPage(page)}
                            style={{ width: '30px', height: '30px', borderRadius: '8px', border: '1px solid ' + (currentPage === page ? BLUE : BORDER), background: currentPage === page ? BLUE : WHITE, color: currentPage === page ? WHITE : TEXT, fontSize: '12px', fontWeight: currentPage === page ? 700 : 400, cursor: 'pointer' }}>
                            {page}
                          </button>
                        )
                      })}
                      <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                        style={{ width: '30px', height: '30px', borderRadius: '8px', border: '1px solid ' + BORDER, background: WHITE, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.4 : 1 }}>
                        <Icon path={IC.chevR} color={TEXT} size={13} strokeWidth={2} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* ── NEW BOOST ── */}
        {mainTab === 'new' && (
          <>
            {/* Step indicator */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0', marginBottom: '20px', background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '4px', overflowX: 'auto' }}>
              {STEP_LABELS.map((label, i) => {
                const s = i + 1
                const active = step === s, complete = step > s
                return (
                  <div key={s} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '8px 6px', borderRadius: '9px', background: active ? BLUE : 'transparent', cursor: complete ? 'pointer' : 'default', minWidth: '80px' }}
                    onClick={() => complete && setStep(s)}>
                    <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: active ? 'rgba(255,255,255,0.25)' : complete ? GREEN : BORDER, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {complete
                        ? <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                        : <span style={{ fontSize: '10px', fontWeight: 700, color: active ? WHITE : MUTED }}>{s}</span>}
                    </div>
                    <span className="step-label" style={{ fontSize: '11.5px', fontWeight: active ? 700 : 500, color: active ? WHITE : complete ? TEXT : MUTED, whiteSpace: 'nowrap' }}>{label}</span>
                  </div>
                )
              })}
            </div>

            <InfoBox color="#FFFBEB" border="#FDE68A" text="#92400E" title="Important">
              Please submit your request at least 24 hours before your desired start date. Our team sets up campaigns within 24 hours of payment confirmation.
            </InfoBox>

            {/* Step 1 */}
            {step === 1 && (
              <div style={{ background: WHITE, borderRadius: '14px', border: '1px solid ' + BORDER, overflow: 'hidden', marginBottom: '16px' }}>
                <div style={{ padding: '14px 20px', borderBottom: '1px solid ' + BORDER, background: BG }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>What do you want to boost?</div>
                  <div style={{ fontSize: '11.5px', color: MUTED, marginTop: '2px' }}>Select the type of content to promote on Facebook</div>
                </div>
                <div style={{ padding: '20px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', marginBottom: '20px' }}>
                    {BOOST_TYPES.map(type => (
                      <button key={type.key} onClick={() => { set('boost_type', type.key); set('goal', '') }}
                        style={{ padding: '14px 16px', borderRadius: '10px', border: `2px solid ${form.boost_type === type.key ? BLUE : BORDER}`, background: form.boost_type === type.key ? '#EFF6FF' : WHITE, cursor: 'pointer', textAlign: 'left', fontFamily: 'Space Grotesk, system-ui, sans-serif', transition: 'all 0.12s' }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: form.boost_type === type.key ? BLUE : TEXT, marginBottom: '4px' }}>{type.label}</div>
                        <div style={{ fontSize: '11.5px', color: MUTED, lineHeight: 1.4 }}>{type.desc}</div>
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button onClick={() => form.boost_type && setStep(2)} disabled={!form.boost_type}
                      style={{ padding: '10px 28px', borderRadius: '10px', background: form.boost_type ? BLUE : BORDER, border: 'none', fontSize: '13px', fontWeight: 700, color: WHITE, cursor: form.boost_type ? 'pointer' : 'not-allowed', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                      Continue
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2 */}
            {step === 2 && (
              <div style={{ background: WHITE, borderRadius: '14px', border: '1px solid ' + BORDER, overflow: 'hidden', marginBottom: '16px' }}>
                <div style={{ padding: '14px 20px', borderBottom: '1px solid ' + BORDER, background: BG }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>Campaign Details</div>
                  <div style={{ fontSize: '11.5px', color: MUTED, marginTop: '2px' }}>Tell us what to boost and your primary goal</div>
                </div>
                <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

                  {/* Facebook Page Access notice */}
                  <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '10px', padding: '14px 16px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: BLUE, marginBottom: '8px' }}>Facebook Page Access Required</div>
                    <div style={{ fontSize: '12px', color: '#1E40AF', lineHeight: 1.65, marginBottom: '10px' }}>
                      To run ads on your behalf, PageConnect needs Partner access to your Facebook Page. This is a one-time setup that takes 2 minutes:
                    </div>
                    {[
                      'Go to your Facebook Page Settings',
                      'Click "Page Roles" then "Partners"',
                      'Enter PageConnect Business ID: (sent to you after payment)',
                      'Grant: Advertiser access',
                      'Click Confirm',
                    ].map((step, i) => (
                      <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ fontSize: '11.5px', fontWeight: 700, color: BLUE, flexShrink: 0 }}>{i + 1}.</span>
                        <span style={{ fontSize: '11.5px', color: '#1E40AF' }}>{step}</span>
                      </div>
                    ))}
                    <div style={{ fontSize: '11.5px', color: '#60A5FA', marginTop: '8px' }}>
                      You can also skip this for now — our team will send instructions with payment confirmation.
                    </div>
                  </div>

                  {['post', 'video_reel', 'live_session', 'page', 'website'].includes(form.boost_type) && (
                    <div>
                      <Label>{form.boost_type === 'page' ? 'Your Facebook Page URL' : 'Facebook Post / Video URL'}</Label>
                      <input type="url" value={form.fb_url} onChange={e => set('fb_url', e.target.value)}
                        placeholder={form.boost_type === 'page' ? 'https://facebook.com/yourpage' : 'https://facebook.com/yourpage/posts/...'}
                        style={{ ...inp }} onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
                    </div>
                  )}

                  {form.boost_type === 'product' && (
                    <div>
                      <Label>Product Name</Label>
                      <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '6px' }}>Tell us which product you want to promote. Our team will target the right audience.</div>
                      <select value={form.product_id} onChange={e => {
                        const p = products.find(p => p.id === e.target.value)
                        set('product_id', e.target.value)
                        set('product_name', p?.name || '')
                      }} style={{ ...inp }}>
                        <option value="">Select a product...</option>
                        {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                      <div style={{ fontSize: '11px', color: MUTED, marginTop: '6px' }}>You can also paste your Facebook post URL below if you have already posted about this product.</div>
                      <input type="url" value={form.fb_url} onChange={e => set('fb_url', e.target.value)}
                        placeholder="Facebook post URL (optional)" style={{ ...inp, marginTop: '8px' }}
                        onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
                    </div>
                  )}

                  <div>
                    <Label>Campaign Goal</Label>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {(GOALS_BY_TYPE[form.boost_type] || []).map(g => (
                        <button key={g.key} onClick={() => set('goal', g.key)}
                          style={{ padding: '8px 16px', borderRadius: '8px', border: `2px solid ${form.goal === g.key ? BLUE : BORDER}`, background: form.goal === g.key ? '#EFF6FF' : WHITE, fontSize: '12.5px', fontWeight: 600, color: form.goal === g.key ? BLUE : TEXT, cursor: 'pointer', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                          {g.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label optional>Current Facebook Page Followers</Label>
                    <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '6px' }}>We track follower growth from your campaign</div>
                    <input type="number" value={form.fb_page_followers_start} onChange={e => set('fb_page_followers_start', e.target.value)}
                      placeholder="e.g. 4200" style={{ ...inp, maxWidth: '200px' }}
                      onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
                  </div>

                  <div>
                    <Label optional>Audience Notes</Label>
                    <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '6px' }}>Any specific targeting preferences for our team</div>
                    <input type="text" value={form.audience_notes} onChange={e => set('audience_notes', e.target.value)}
                      placeholder="e.g. Women 20–35, Dhaka and Chittagong only, interested in fashion"
                      style={{ ...inp }} onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
                  </div>

                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', paddingTop: '4px' }}>
                    <button onClick={() => setStep(1)} style={{ padding: '10px 20px', borderRadius: '10px', background: BG, border: '1px solid ' + BORDER, fontSize: '13px', fontWeight: 600, color: TEXT, cursor: 'pointer', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>Back</button>
                    <button onClick={() => form.goal && setStep(3)} disabled={!form.goal}
                      style={{ padding: '10px 28px', borderRadius: '10px', background: form.goal ? BLUE : BORDER, border: 'none', fontSize: '13px', fontWeight: 700, color: WHITE, cursor: form.goal ? 'pointer' : 'not-allowed', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                      Continue
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3 */}
            {step === 3 && (
              <div style={{ background: WHITE, borderRadius: '14px', border: '1px solid ' + BORDER, overflow: 'hidden', marginBottom: '16px' }}>
                <div style={{ padding: '14px 20px', borderBottom: '1px solid ' + BORDER, background: BG }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>Budget & Duration</div>
                  {feeSettings && <div style={{ fontSize: '11.5px', color: MUTED, marginTop: '2px' }}>Minimum ৳{feeSettings.min_budget_bdt} · {feeSettings.min_duration_days}–{feeSettings.max_duration_days} days</div>}
                </div>
                <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div>
                    <Label>Ad Budget (৳)</Label>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                      {BUDGET_PRESETS.map(b => (
                        <button key={b} onClick={() => set('budget_bdt', b)}
                          style={{ padding: '8px 16px', borderRadius: '8px', border: `2px solid ${Number(form.budget_bdt) === b ? BLUE : BORDER}`, background: Number(form.budget_bdt) === b ? '#EFF6FF' : WHITE, fontSize: '13px', fontWeight: 600, color: Number(form.budget_bdt) === b ? BLUE : TEXT, cursor: 'pointer', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                          ৳{b.toLocaleString()}
                        </button>
                      ))}
                    </div>
                    <input type="number" value={form.budget_bdt} onChange={e => set('budget_bdt', e.target.value)}
                      placeholder="Or enter custom amount" style={{ ...inp, maxWidth: '220px' }}
                      onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
                  </div>

                  <div>
                    <Label>Duration</Label>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                      {DURATION_PRESETS.map(d => (
                        <button key={d} onClick={() => set('duration_days', d)}
                          style={{ padding: '8px 16px', borderRadius: '8px', border: `2px solid ${Number(form.duration_days) === d ? BLUE : BORDER}`, background: Number(form.duration_days) === d ? '#EFF6FF' : WHITE, fontSize: '13px', fontWeight: 600, color: Number(form.duration_days) === d ? BLUE : TEXT, cursor: 'pointer', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                          {d} days
                        </button>
                      ))}
                    </div>
                    <input type="number" value={form.duration_days} onChange={e => set('duration_days', e.target.value)}
                      placeholder="Or enter custom days" style={{ ...inp, maxWidth: '180px' }}
                      onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
                  </div>

                  <div>
                    <Label optional>Preferred Start Date</Label>
                    <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '6px' }}>Leave blank to start as soon as possible after payment</div>
                    <input type="date" value={form.scheduled_start_date} onChange={e => set('scheduled_start_date', e.target.value)}
                      min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                      style={{ ...inp, maxWidth: '200px' }}
                      onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
                  </div>

                  {pricing.budget > 0 && (
                    <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '12px', padding: '16px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 700, color: BLUE, marginBottom: '12px' }}>Cost Breakdown</div>
                      {[
                        { label: 'Ad Budget (goes entirely to Facebook)', val: '৳' + pricing.budget.toLocaleString() },
                        { label: `Service Fee — PageConnect (${pricing.svcPct}%)`, val: '৳' + pricing.svcFee.toFixed(0) },
                        { label: `VAT on service fee (${pricing.vatPct}%)`, val: '৳' + pricing.vat.toFixed(0) },
                      ].map(r => (
                        <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #BFDBFE', gap: '12px' }}>
                          <span style={{ fontSize: '12px', color: '#1E40AF' }}>{r.label}</span>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: '#1E40AF', flexShrink: 0 }}>{r.val}</span>
                        </div>
                      ))}
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0' }}>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: BLUE }}>Total Payable</span>
                        <span style={{ fontSize: '16px', fontWeight: 800, color: BLUE }}>৳{pricing.total.toFixed(0)}</span>
                      </div>
                      <div style={{ fontSize: '11px', color: '#60A5FA', marginTop: '8px' }}>
                        Equivalent ad spend: ~${pricing.usd.toFixed(2)} USD at PageConnect rate ৳{pricing.rate}/USD (includes bank & conversion fees)
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between' }}>
                    <button onClick={() => setStep(2)} style={{ padding: '10px 20px', borderRadius: '10px', background: BG, border: '1px solid ' + BORDER, fontSize: '13px', fontWeight: 600, color: TEXT, cursor: 'pointer', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>Back</button>
                    <button onClick={() => {
                      const minBudget = feeSettings?.min_budget_bdt || 500
                      const minDur   = feeSettings?.min_duration_days || 3
                      if (!form.budget_bdt || Number(form.budget_bdt) < minBudget) { showToast(`Minimum budget is ৳${minBudget}`, 'error'); return }
                      if (!form.duration_days || Number(form.duration_days) < minDur) { showToast(`Minimum duration is ${minDur} days`, 'error'); return }
                      setStep(4)
                    }} style={{ padding: '10px 28px', borderRadius: '10px', background: BLUE, border: 'none', fontSize: '13px', fontWeight: 700, color: WHITE, cursor: 'pointer', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                      Continue
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4 */}
            {step === 4 && (
              <div style={{ background: WHITE, borderRadius: '14px', border: '1px solid ' + BORDER, overflow: 'hidden', marginBottom: '16px' }}>
                <div style={{ padding: '14px 20px', borderBottom: '1px solid ' + BORDER, background: BG }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>Creative</div>
                  <div style={{ fontSize: '11.5px', color: MUTED, marginTop: '2px' }}>If boosting an existing post you can skip this step</div>
                </div>
                <div style={{ padding: '20px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', marginBottom: '20px' }}>
                    {[
                      { key: 'upload',  label: 'Upload Creative',      desc: 'You have an image or video ready' },
                      { key: 'request', label: 'Request Custom Design', desc: 'Our team designs it — quoted separately, no auto-charge' },
                      { key: 'skip',    label: 'No Creative Needed',   desc: 'Boosting an existing post or page' },
                    ].map(opt => (
                      <button key={opt.key} onClick={() => set('creative_option', opt.key)}
                        style={{ padding: '14px 16px', borderRadius: '10px', border: `2px solid ${form.creative_option === opt.key ? BLUE : BORDER}`, background: form.creative_option === opt.key ? '#EFF6FF' : WHITE, cursor: 'pointer', textAlign: 'left', fontFamily: 'Space Grotesk, system-ui, sans-serif', transition: 'all 0.12s' }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: form.creative_option === opt.key ? BLUE : TEXT, marginBottom: '4px' }}>{opt.label}</div>
                        <div style={{ fontSize: '11.5px', color: MUTED, lineHeight: 1.4 }}>{opt.desc}</div>
                      </button>
                    ))}
                  </div>

                  {form.creative_option === 'upload' && (
                    <div style={{ background: BG, borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                      <Label>Upload Image or Video</Label>
                      <input type="file" accept="image/*,video/*" onChange={e => set('creative_url', e.target.files?.[0])} style={{ fontSize: '12px', color: TEXT }} />
                      <div style={{ fontSize: '11px', color: MUTED, marginTop: '6px' }}>Maximum 10MB. JPG, PNG, MP4 supported.</div>
                    </div>
                  )}

                  {form.creative_option === 'request' && (
                    <div style={{ background: BG, borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                      <Label>Describe what you need</Label>
                      <textarea value={form.creative_request} onChange={e => set('creative_request', e.target.value)}
                        placeholder="e.g. Blue background, product photo centred, price tag, shop name. Eid theme. Bengali text: বিশেষ ছাড়"
                        rows={4} style={{ ...inp, resize: 'vertical' }}
                        onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
                      <div style={{ fontSize: '11.5px', color: AMBER, marginTop: '8px', lineHeight: 1.5 }}>
                        Custom design is quoted separately. Our team will contact you with pricing — no charge is applied automatically.
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between' }}>
                    <button onClick={() => setStep(3)} style={{ padding: '10px 20px', borderRadius: '10px', background: BG, border: '1px solid ' + BORDER, fontSize: '13px', fontWeight: 600, color: TEXT, cursor: 'pointer', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>Back</button>
                    <button onClick={() => form.creative_option && setStep(5)} disabled={!form.creative_option}
                      style={{ padding: '10px 28px', borderRadius: '10px', background: form.creative_option ? BLUE : BORDER, border: 'none', fontSize: '13px', fontWeight: 700, color: WHITE, cursor: form.creative_option ? 'pointer' : 'not-allowed', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                      Continue
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 5 — Review & Pay */}
            {step === 5 && (
              <div style={{ background: WHITE, borderRadius: '14px', border: '1px solid ' + BORDER, overflow: 'hidden', marginBottom: '16px' }}>
                <div style={{ padding: '14px 20px', borderBottom: '1px solid ' + BORDER, background: BG }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>Review & Pay</div>
                  <div style={{ fontSize: '11.5px', color: MUTED, marginTop: '2px' }}>Confirm your campaign and choose payment method</div>
                </div>
                <div style={{ padding: '20px' }}>
                  {/* Summary */}
                  <div style={{ background: BG, borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '10px' }}>Campaign Summary</div>
                    {[
                      { label: 'Boost Type', val: BOOST_TYPES.find(t => t.key === form.boost_type)?.label },
                      { label: 'Goal', val: form.goal?.replace(/_/g, ' ') },
                      form.fb_url && { label: 'URL', val: form.fb_url.length > 50 ? form.fb_url.slice(0, 50) + '...' : form.fb_url },
                      form.product_name && { label: 'Product', val: form.product_name },
                      { label: 'Budget', val: '৳' + Number(form.budget_bdt).toLocaleString() },
                      { label: 'Duration', val: form.duration_days + ' days' },
                      form.scheduled_start_date && { label: 'Start Date', val: form.scheduled_start_date },
                      { label: 'Creative', val: form.creative_option === 'upload' ? 'Uploaded' : form.creative_option === 'request' ? 'Requested (quoted separately)' : 'Not required' },
                    ].filter(Boolean).map(r => (
                      <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid ' + BORDER, gap: '12px' }}>
                        <span style={{ fontSize: '12px', color: MUTED, flexShrink: 0 }}>{r.label}</span>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: TEXT, textAlign: 'right', textTransform: 'capitalize' }}>{r.val}</span>
                      </div>
                    ))}
                  </div>

                  {/* Pricing */}
                  <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: BLUE, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '10px' }}>Payment Summary</div>
                    {[
                      { label: 'Ad Budget', val: '৳' + pricing.budget.toLocaleString() },
                      { label: `Service Fee (${pricing.svcPct}%)`, val: '৳' + pricing.svcFee.toFixed(0) },
                      { label: `VAT (${pricing.vatPct}%)`, val: '৳' + pricing.vat.toFixed(0) },
                    ].map(r => (
                      <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #BFDBFE' }}>
                        <span style={{ fontSize: '12px', color: '#1E40AF' }}>{r.label}</span>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#1E40AF' }}>{r.val}</span>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0 0' }}>
                      <span style={{ fontSize: '14px', fontWeight: 700, color: BLUE }}>Total Payable</span>
                      <span style={{ fontSize: '18px', fontWeight: 800, color: BLUE }}>৳{pricing.total.toFixed(0)}</span>
                    </div>
                  </div>

                  {/* Payment method */}
                  <div style={{ marginBottom: '16px' }}>
                    <Label>Payment Method</Label>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                      {[
                        { key: 'bkash',         label: 'bKash',          color: '#E2136E', market: 'BD' },
                        { key: 'nagad',         label: 'Nagad',          color: '#F7941D', market: 'BD' },
                        { key: 'rocket',        label: 'Rocket',         color: '#8B2FC9', market: 'BD' },
                        { key: 'upay',          label: 'Upay',           color: '#0066CC', market: 'BD' },
                        { key: 'bank_transfer', label: 'Bank Transfer',  color: '#065F46', market: 'both' },
                        { key: 'payid',         label: 'PayID',          color: '#FF6900', market: 'INT' },
                        { key: 'zelle',         label: 'Zelle',          color: '#6D1ED4', market: 'INT' },
                        { key: 'wise',          label: 'Wise',           color: '#009E6A', market: 'INT' },
                      ].filter(pm => {
                        const market = seller?.country === 'BD' || !seller?.country ? 'BD' : 'INT'
                        return pm.market === market || pm.market === 'both'
                      }).map(pm => (
                        <button key={pm.key} onClick={() => set('payment_method', pm.key)}
                          style={{ padding: '8px 18px', borderRadius: '10px', border: `2px solid ${form.payment_method === pm.key ? pm.color : BORDER}`, background: form.payment_method === pm.key ? pm.color : WHITE, fontSize: '12.5px', fontWeight: 700, color: form.payment_method === pm.key ? WHITE : TEXT, cursor: 'pointer', fontFamily: 'Space Grotesk, system-ui, sans-serif', transition: 'all 0.12s' }}>
                          {pm.label}
                        </button>
                      ))}
                    </div>
                    <div style={{ fontSize: '12px', color: MUTED, lineHeight: 1.55 }}>
                      After submitting, our team will send payment instructions to your WhatsApp or via in-app notification. Do not send payment until you receive the instructions with the exact number and reference.
                    </div>
                  </div>

                  {/* Terms */}
                  <div style={{ background: BG, border: '1px solid ' + BORDER, borderRadius: '10px', padding: '14px 16px', marginBottom: '20px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: TEXT, marginBottom: '8px' }}>Terms & Conditions</div>
                    <div style={{ fontSize: '11.5px', color: MUTED, lineHeight: 1.65 }}>
                      By submitting this request, you confirm:<br /><br />
                      1. You are the authorised owner or administrator of the Facebook page/content being boosted.<br />
                      2. Your content complies with Facebook's advertising policies. PageConnect is not responsible for ad rejections due to content violations.<br />
                      3. Campaign performance (reach, clicks, impressions) depends on Facebook's algorithm and audience. PageConnect does not guarantee specific results.<br />
                      4. Service fee and VAT are non-refundable once a campaign is live. If PageConnect cannot launch your campaign, a full refund will be issued.<br />
                      5. All content responsibility, including copyright and accuracy of claims, remains with you.
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between' }}>
                    <button onClick={() => setStep(4)} style={{ padding: '10px 20px', borderRadius: '10px', background: BG, border: '1px solid ' + BORDER, fontSize: '13px', fontWeight: 600, color: TEXT, cursor: 'pointer', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>Back</button>
                    <button onClick={handleSubmit} disabled={submitting}
                      style={{ padding: '12px 32px', borderRadius: '10px', background: submitting ? BORDER : BLUE, border: 'none', fontSize: '14px', fontWeight: 700, color: WHITE, cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                      {submitting ? 'Submitting...' : 'Submit Campaign Request'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── INSIGHTS ── */}
        {mainTab === 'insights' && (
          <div>
            <InfoBox title="Note on these insights">
              These are suggestions based on your recent sales data. They are recommendations only — you have full freedom to boost any content. PageConnect does not guarantee that boosting these products will result in specific sales outcomes.
            </InfoBox>
            {insights.length === 0 ? (
              <div style={{ background: WHITE, borderRadius: '14px', border: '1px solid ' + BORDER, padding: '40px', textAlign: 'center', color: MUTED, fontSize: '13px' }}>
                Not enough order data yet. Start taking orders to see personalised insights.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {insights.map((insight, i) => (
                  <div key={i} style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px 20px', display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon path={IC.trending} color={BLUE} size={16} strokeWidth={2} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT, marginBottom: '4px' }}>{insight.title}</div>
                      <div style={{ fontSize: '12px', color: MUTED, lineHeight: 1.55 }}>{insight.desc}</div>
                    </div>
                    <button onClick={() => { setMainTab('new'); setStep(1) }}
                      style={{ padding: '7px 14px', borderRadius: '8px', background: BLUE, border: 'none', fontSize: '12px', fontWeight: 600, color: WHITE, cursor: 'pointer', flexShrink: 0, fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                      Boost Now
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── HOW IT WORKS ── */}
        {mainTab === 'howto' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ background: WHITE, borderRadius: '14px', border: '1px solid ' + BORDER, padding: '20px 24px' }}>
              <div style={{ fontSize: '14px', fontWeight: 800, color: TEXT, marginBottom: '10px' }}>What is AdBoost?</div>
              <div style={{ fontSize: '13px', color: MUTED, lineHeight: 1.7 }}>
                AdBoost is PageConnect's managed Facebook advertising service. You submit a boost request and our team sets up and runs the campaign on your behalf using our Facebook Business Manager. You pay in bKash, Nagad, Rocket, or Upay — no USD card needed.
              </div>
            </div>

            <div style={{ background: WHITE, borderRadius: '14px', border: '1px solid ' + BORDER, padding: '20px 24px' }}>
              <div style={{ fontSize: '14px', fontWeight: 800, color: TEXT, marginBottom: '16px' }}>How the process works</div>
              {[
                { title: 'Submit a request',    desc: 'Fill in what to boost, your budget, duration and goal. Takes 2 minutes.' },
                { title: 'Receive payment info', desc: 'Our team sends payment instructions to your WhatsApp within a few hours. Send bKash/Nagad to the number provided with the reference code.' },
                { title: 'Campaign goes live',   desc: 'We set up your campaign in Facebook Business Manager within 24 hours of payment confirmation. You get notified when it launches.' },
                { title: 'Track progress',       desc: 'Monitor your campaign status and days remaining directly in the All Campaigns tab.' },
                { title: 'Receive results',      desc: 'When the campaign ends, we update your dashboard with reach, impressions, clicks, and follower growth. A full report is available in the campaign detail.' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: '14px', marginBottom: '14px' }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: BLUE, color: WHITE, fontSize: '12px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>{i + 1}</div>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT, marginBottom: '3px' }}>{item.title}</div>
                    <div style={{ fontSize: '12px', color: MUTED, lineHeight: 1.55 }}>{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ background: WHITE, borderRadius: '14px', border: '1px solid ' + BORDER, padding: '20px 24px' }}>
              <div style={{ fontSize: '14px', fontWeight: 800, color: TEXT, marginBottom: '10px' }}>Pricing & USD Rate Explained</div>
              <div style={{ fontSize: '13px', color: MUTED, lineHeight: 1.7, marginBottom: '12px' }}>
                When you set a budget of ৳1,000, that full amount goes to Facebook as ad spend. On top of that, PageConnect charges a service fee for managing your campaign, plus VAT as required by Bangladesh law.
              </div>
              <div style={{ background: BG, borderRadius: '10px', padding: '14px', marginBottom: '14px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: TEXT, marginBottom: '8px' }}>Example — ৳1,000 budget</div>
                {[
                  { label: 'Ad Budget (to Facebook)', val: '৳1,000' },
                  { label: 'Service Fee (15%)', val: '৳150' },
                  { label: 'VAT on service fee (15%)', val: '৳22' },
                  { label: 'Total you pay', val: '৳1,172', bold: true },
                ].map(r => (
                  <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid ' + BORDER }}>
                    <span style={{ fontSize: '12px', color: MUTED }}>{r.label}</span>
                    <span style={{ fontSize: '12px', fontWeight: r.bold ? 800 : 600, color: r.bold ? BLUE : TEXT }}>{r.val}</span>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT, marginBottom: '6px' }}>Why is there a PageConnect USD rate?</div>
              <div style={{ fontSize: '12.5px', color: MUTED, lineHeight: 1.7 }}>
                Facebook charges for advertising in USD. PageConnect pays Facebook on your behalf using our international payment infrastructure. We convert your BDT payment to USD at a fixed rate that includes international bank fees, card transaction charges, and a small buffer for exchange rate fluctuation. This is our <strong style={{ color: TEXT }}>PageConnect rate</strong> — not the market rate. It is reviewed weekly and shown transparently before you submit. The rate is locked at submission time and does not change for your campaign.
              </div>
            </div>

            <div style={{ background: WHITE, borderRadius: '14px', border: '1px solid ' + BORDER, padding: '20px 24px' }}>
              <div style={{ fontSize: '14px', fontWeight: 800, color: TEXT, marginBottom: '10px' }}>Important Limitations</div>
              <div style={{ fontSize: '12.5px', color: MUTED, lineHeight: 1.7 }}>
                <strong style={{ color: TEXT }}>We do not guarantee specific results.</strong> Reach, clicks, and follower numbers depend on Facebook's algorithm, your content quality, and audience. AdBoost gives your content more visibility — results vary.<br /><br />
                <strong style={{ color: TEXT }}>We do not guarantee ad approval.</strong> Facebook reviews all ads. If your content violates their policies, the ad may be rejected. We will notify you and work to resolve it.<br /><br />
                <strong style={{ color: TEXT }}>Refund policy.</strong> If we cannot launch your campaign, a full refund is issued. If Facebook rejects your ad due to your content, we will work with you to resubmit — refunds in this case are at our discretion.<br /><br />
                <strong style={{ color: TEXT }}>Your responsibility.</strong> You are responsible for ensuring your content complies with Facebook's advertising policies, Bangladesh advertising regulations, and that all product claims are accurate.
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '8px' }}>
              <button onClick={() => { setMainTab('new'); setStep(1) }}
                style={{ padding: '12px 32px', borderRadius: '10px', background: BLUE, border: 'none', fontSize: '14px', fontWeight: 700, color: WHITE, cursor: 'pointer', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                Create a Campaign
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedBoost && <DetailModal boost={selectedBoost} onClose={() => setSelectedBoost(null)} />}

      <style>{`
        .hidden { display: none !important; }
        .md\\:hidden { display: block !important; }
        @media(min-width: 768px) {
          .hidden { display: block !important; }
          .hidden.md\\:grid { display: grid !important; }
          .md\\:hidden { display: none !important; }
        }
        .step-label { display: none; }
        @media(min-width: 520px) { .step-label { display: inline !important; } }
        @media(max-width: 1100px) {
          .ab-stat-grid { grid-template-columns: repeat(3, 1fr) !important; }
          .ab-chart-grid { grid-template-columns: 1fr !important; }
        }
        @media(max-width: 640px) {
          .ab-stat-grid { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>
    </div>
  )
}